import { useEffect, useMemo, useState } from 'react'
import { type FilterCondition, useReportBuilderStore } from '../../stores/reportBuilderStore'
import { type AvailableField, FilterOperator, FilterSource, getOperatorLabel, OPERATORS_BY_TYPE } from '../../types'

const DEFAULT_OPERATORS = [
	{ value: FilterOperator.EQ, label: 'Equals' },
	{ value: FilterOperator.NEQ, label: 'Not equals' },
	{ value: FilterOperator.CONTAINS, label: 'Contains' },
	{ value: FilterOperator.IN, label: 'In list' },
]

export function StepFilters() {
	const { currentConfig, updateConfig, availableFields, formFields, loadFormFields, nextStep, prevStep } =
		useReportBuilderStore()

	// V2.5.2: Load form fields when forms are selected
	useEffect(() => {
		if (currentConfig.selectedFormIds.length > 0) {
			loadFormFields(currentConfig.selectedFormIds)
		}
	}, [currentConfig.selectedFormIds, loadFormFields])

	const [newFilter, setNewFilter] = useState<Partial<FilterCondition>>({
		field: '',
		operator: FilterOperator.EQ,
		values: [],
		source: FilterSource.USER_ATTRIBUTE,
	})
	const [newFilterValue, setNewFilterValue] = useState('')
	const [secondValue, setSecondValue] = useState('')
	const [validationError, setValidationError] = useState<string | null>(null)

	const selectedField = useMemo<AvailableField | undefined>(() => {
		// Check availableFields first
		const field = availableFields.find((f) => f.id === newFilter.field)
		if (field) return field

		// V2.5.2: Check form fields (with form. prefix)
		if (newFilter.field?.startsWith('form.')) {
			const formFieldId = newFilter.field.slice(5)
			const formField = formFields.find((f) => f.id === formFieldId)
			if (formField) {
				return {
					id: newFilter.field,
					label: formField.sectionLabel ? `${formField.sectionLabel} > ${formField.label}` : formField.label,
					type: formField.isNumeric ? 'number' : 'text',
					source: FilterSource.FORM_COMPONENT,
					options: formField.options,
				}
			}
		}

		return undefined
	}, [availableFields, formFields, newFilter.field])

	const operators = useMemo(() => {
		if (!selectedField) return DEFAULT_OPERATORS
		return OPERATORS_BY_TYPE[selectedField.type] || DEFAULT_OPERATORS
	}, [selectedField])

	const fieldType = selectedField?.type || 'text'
	const isDateField = fieldType === 'date'
	const isNumberField = fieldType === 'number'
	const isBooleanField = fieldType === 'boolean'
	const isSelectField = fieldType === 'select'
	const isBetweenOperator = newFilter.operator === FilterOperator.BETWEEN
	const isInOperator = newFilter.operator === FilterOperator.IN
	const isNullOperator =
		newFilter.operator === FilterOperator.IS_NULL || newFilter.operator === FilterOperator.IS_NOT_NULL

	const validateValue = (value: string): string | null => {
		if (isNullOperator) return null

		if (!value.trim()) {
			return 'Value cannot be empty'
		}

		if (isNumberField) {
			const num = Number(value)
			if (Number.isNaN(num)) {
				return 'Please enter a valid number'
			}
		}

		return null
	}

	const validateBetweenValues = (first: string, second: string): string | null => {
		if (!first || !second) {
			return 'Both values are required for range filter'
		}

		if (isDateField) {
			const date1 = new Date(first)
			const date2 = new Date(second)
			if (date2 < date1) {
				return 'End date must be after start date'
			}
		}

		if (isNumberField) {
			const num1 = Number(first)
			const num2 = Number(second)
			if (Number.isNaN(num1) || Number.isNaN(num2)) {
				return 'Please enter valid numbers'
			}
			if (num2 < num1) {
				return 'Second value must be greater than first value'
			}
		}

		return null
	}

	const canAddFilter = useMemo(() => {
		if (!newFilter.field) return false
		if (isNullOperator) return true

		if (isBetweenOperator) {
			const values = newFilter.values || []
			return values.length === 2 && values[0] && values[1]
		}

		if (isBooleanField || isSelectField) {
			return (newFilter.values || []).length > 0
		}

		if (isInOperator) {
			return (newFilter.values || []).length > 0
		}

		return (newFilter.values || []).length > 0
	}, [newFilter, isNullOperator, isBetweenOperator, isBooleanField, isSelectField, isInOperator])

	const addFilter = () => {
		if (!canAddFilter) return

		setValidationError(null)

		if (isBetweenOperator && newFilter.values) {
			const error = validateBetweenValues(newFilter.values[0], newFilter.values[1])
			if (error) {
				setValidationError(error)
				return
			}
		} else if (!isNullOperator && !isInOperator && !isBooleanField && !isSelectField) {
			const singleValue = newFilter.values?.[0]
			if (singleValue !== undefined) {
				const error = validateValue(singleValue)
				if (error) {
					setValidationError(error)
					return
				}
			}
		}

		const filter: FilterCondition = {
			field: newFilter.field as string,
			operator: newFilter.operator || FilterOperator.EQ,
			values: isNullOperator ? [] : newFilter.values || [],
			source: selectedField?.source || FilterSource.USER_ATTRIBUTE,
		}

		updateConfig({ filters: [...currentConfig.filters, filter] })
		setNewFilter({
			field: '',
			operator: FilterOperator.EQ,
			values: [],
			source: FilterSource.USER_ATTRIBUTE,
		})
		setNewFilterValue('')
		setSecondValue('')
		setValidationError(null)
	}

	const removeFilter = (index: number) => {
		const filters = currentConfig.filters.filter((_, i) => i !== index)
		updateConfig({ filters })
	}

	const addValueToList = () => {
		const error = validateValue(newFilterValue)
		if (error) {
			setValidationError(error)
			return
		}

		const trimmedValue = newFilterValue.trim()
		if (newFilter.values?.includes(trimmedValue)) {
			setValidationError('Value already added')
			return
		}

		setValidationError(null)
		setNewFilter({
			...newFilter,
			values: [...(newFilter.values || []), trimmedValue],
		})
		setNewFilterValue('')
	}

	const setBetweenValues = () => {
		const error = validateBetweenValues(newFilterValue, secondValue)
		if (error) {
			setValidationError(error)
			return
		}

		setValidationError(null)
		setNewFilter({
			...newFilter,
			values: [newFilterValue, secondValue],
		})
	}

	const removeValueFromNewFilter = (index: number) => {
		setNewFilter({
			...newFilter,
			values: (newFilter.values || []).filter((_, i) => i !== index),
		})
	}

	const handleFieldChange = (fieldId: string) => {
		let field = availableFields.find((f) => f.id === fieldId)
		let source = field?.source || FilterSource.USER_ATTRIBUTE

		if (!field && fieldId.startsWith('form.')) {
			const formFieldId = fieldId.slice(5)
			const formField = formFields.find((f) => f.id === formFieldId)
			if (formField) {
				field = {
					id: fieldId,
					label: formField.label,
					type: formField.isNumeric ? 'number' : 'text',
					source: FilterSource.FORM_COMPONENT,
					options: formField.options,
				}
				source = FilterSource.FORM_COMPONENT
			}
		}

		const defaultOperator = field ? (OPERATORS_BY_TYPE[field.type]?.[0]?.value ?? FilterOperator.EQ) : FilterOperator.EQ

		setNewFilter({
			field: fieldId,
			operator: defaultOperator,
			values: [],
			source,
		})
		setNewFilterValue('')
		setSecondValue('')
		setValidationError(null)
	}

	const handleOperatorChange = (operator: FilterOperator) => {
		setNewFilter({ ...newFilter, operator, values: [] })
		setNewFilterValue('')
		setSecondValue('')
		setValidationError(null)
	}

	const getFieldLabel = (fieldId: string): string => {
		// Check availableFields first
		const field = availableFields.find((f) => f.id === fieldId)
		if (field) return field.label

		// V2.5.2: Check form fields (with form. prefix)
		if (fieldId.startsWith('form.')) {
			const formFieldId = fieldId.slice(5)
			const formField = formFields.find((f) => f.id === formFieldId)
			if (formField) {
				return formField.sectionLabel ? `${formField.sectionLabel} > ${formField.label}` : formField.label
			}
		}

		return fieldId
	}

	const formatDisplayValue = (value: string, fieldId: string): string => {
		const field = availableFields.find((f) => f.id === fieldId)
		if (!field) return value

		if (field.type === 'boolean') {
			return value === 'true' ? 'Yes' : 'No'
		}

		if (field.type === 'date') {
			try {
				const [year, month, day] = value.split('-').map(Number)
				const date = new Date(year, month - 1, day)
				return date.toLocaleDateString()
			} catch {
				return value
			}
		}

		return value
	}

	const renderValueInput = () => {
		if (isNullOperator) {
			return <div className='text-sm text-neutral-500 italic py-2'>No value needed for this operator</div>
		}

		if (isBooleanField) {
			return (
				<select
					value={newFilter.values?.[0] || ''}
					onChange={(e) => {
						setValidationError(null)
						setNewFilter({ ...newFilter, values: e.target.value ? [e.target.value] : [] })
					}}
					className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'>
					<option value=''>Select...</option>
					<option value='true'>Yes</option>
					<option value='false'>No</option>
				</select>
			)
		}

		if (isSelectField && selectedField?.options) {
			if (isInOperator) {
				return (
					<div className='space-y-2'>
						<select
							value=''
							onChange={(e) => {
								const value = e.target.value
								if (value && !newFilter.values?.includes(value)) {
									setValidationError(null)
									setNewFilter({ ...newFilter, values: [...(newFilter.values || []), value] })
								}
							}}
							className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'>
							<option value=''>Add value...</option>
							{selectedField.options
								.filter((opt) => !newFilter.values?.includes(opt))
								.map((opt) => (
									<option key={opt} value={opt}>
										{opt}
									</option>
								))}
						</select>
					</div>
				)
			}
			return (
				<select
					value={newFilter.values?.[0] || ''}
					onChange={(e) => {
						setValidationError(null)
						setNewFilter({ ...newFilter, values: e.target.value ? [e.target.value] : [] })
					}}
					className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'>
					<option value=''>Select...</option>
					{selectedField.options.map((opt) => (
						<option key={opt} value={opt}>
							{opt}
						</option>
					))}
				</select>
			)
		}

		if (isDateField) {
			if (isBetweenOperator) {
				const hasValues = newFilter.values?.length === 2
				return (
					<div className='space-y-3'>
						<div className='relative'>
							<span className='absolute -top-2 right-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded'>
								From
							</span>
							<input
								type='date'
								value={newFilterValue}
								onChange={(e) => {
									setValidationError(null)
									const newFrom = e.target.value
									setNewFilterValue(newFrom)
									if (secondValue && newFrom > secondValue) {
										setSecondValue(newFrom)
									}
								}}
								className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							/>
						</div>
						<div className='relative'>
							<span className='absolute -top-2 right-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded'>
								To
							</span>
							<input
								type='date'
								value={secondValue}
								onChange={(e) => {
									setValidationError(null)
									const newTo = e.target.value
									setSecondValue(newTo)
									if (newFilterValue && newTo < newFilterValue) {
										setNewFilterValue(newTo)
									}
								}}
								className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							/>
						</div>
						<button
							type='button'
							onClick={setBetweenValues}
							disabled={!newFilterValue || !secondValue}
							className='w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
							{hasValues ? '✓ Set Range' : 'Set Range'}
						</button>
					</div>
				)
			}
			return (
				<input
					type='date'
					value={newFilter.values?.[0] || ''}
					onChange={(e) => {
						setValidationError(null)
						setNewFilter({ ...newFilter, values: e.target.value ? [e.target.value] : [] })
					}}
					className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
				/>
			)
		}

		if (isNumberField) {
			if (isBetweenOperator) {
				const hasValues = newFilter.values?.length === 2
				return (
					<div className='space-y-3'>
						<div className='relative'>
							<span className='absolute -top-2 right-2 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded'>
								Min
							</span>
							<input
								type='number'
								value={newFilterValue}
								onChange={(e) => {
									setValidationError(null)
									const newMin = e.target.value
									setNewFilterValue(newMin)
									if (secondValue && Number(newMin) > Number(secondValue)) {
										setSecondValue(newMin)
									}
								}}
								placeholder='Minimum value'
								className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							/>
						</div>
						<div className='relative'>
							<span className='absolute -top-2 right-2 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded'>
								Max
							</span>
							<input
								type='number'
								value={secondValue}
								onChange={(e) => {
									setValidationError(null)
									const newMax = e.target.value
									setSecondValue(newMax)
									if (newFilterValue && Number(newMax) < Number(newFilterValue)) {
										setNewFilterValue(newMax)
									}
								}}
								placeholder='Maximum value'
								className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							/>
						</div>
						<button
							type='button'
							onClick={setBetweenValues}
							disabled={!newFilterValue || !secondValue}
							className='w-full px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
							{hasValues ? '✓ Set Range' : 'Set Range'}
						</button>
					</div>
				)
			}
			return (
				<input
					type='number'
					value={newFilter.values?.[0] || ''}
					onChange={(e) => {
						setValidationError(null)
						setNewFilter({ ...newFilter, values: e.target.value ? [e.target.value] : [] })
					}}
					placeholder='Enter number...'
					className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
				/>
			)
		}

		if (isInOperator) {
			return (
				<div className='flex gap-2'>
					<input
						type='text'
						value={newFilterValue}
						onChange={(e) => {
							setValidationError(null)
							setNewFilterValue(e.target.value)
						}}
						onKeyDown={(e) => e.key === 'Enter' && addValueToList()}
						placeholder='Enter value and press + or Enter'
						className='flex-1 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
					/>
					<button
						type='button'
						onClick={addValueToList}
						disabled={!newFilterValue.trim()}
						className='px-3 py-2 bg-neutral-200 text-neutral-700 rounded-md hover:bg-neutral-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed'>
						+
					</button>
				</div>
			)
		}

		return (
			<input
				type='text'
				value={newFilter.values?.[0] || ''}
				onChange={(e) => {
					setValidationError(null)
					setNewFilter({ ...newFilter, values: e.target.value ? [e.target.value] : [] })
				}}
				placeholder='Enter value...'
				className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
			/>
		)
	}

	const groupedFields = useMemo(() => {
		const groups: Record<string, AvailableField[]> = {
			'System Fields': [],
			'Custom Fields': [],
		}
		for (const field of availableFields) {
			if (field.source === FilterSource.SYSTEM_FIELD) {
				groups['System Fields'].push(field)
			} else {
				groups['Custom Fields'].push(field)
			}
		}

		// V2.5.2: Group form fields by form name
		for (const formField of formFields) {
			const groupName = `${formField.formName}`
			if (!groups[groupName]) {
				groups[groupName] = []
			}
			groups[groupName].push({
				id: `form.${formField.id}`,
				label: formField.sectionLabel ? `${formField.sectionLabel} > ${formField.label}` : formField.label,
				type: formField.isNumeric ? 'number' : 'text',
				source: FilterSource.FORM_COMPONENT,
				options: formField.options,
			})
		}

		return groups
	}, [availableFields, formFields])

	const filterLogic = currentConfig.filterLogic || 'AND'

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-lg font-semibold text-neutral-900 mb-2'>Apply Filters</h2>
					<p className='text-sm text-neutral-600'>Filter your report data by field values.</p>
				</div>
				{currentConfig.filters.length > 1 && (
					<div className='flex items-center gap-2'>
						<span className='text-sm text-neutral-600'>Combine filters with:</span>
						<div className='inline-flex rounded-lg border border-neutral-300 overflow-hidden'>
							<button
								type='button'
								onClick={() => updateConfig({ filterLogic: 'AND' })}
								className={`px-3 py-1.5 text-sm font-medium transition-colors ${
									filterLogic === 'AND' ? 'bg-blue-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'
								}`}>
								AND
							</button>
							<button
								type='button'
								onClick={() => updateConfig({ filterLogic: 'OR' })}
								className={`px-3 py-1.5 text-sm font-medium transition-colors border-l border-neutral-300 ${
									filterLogic === 'OR' ? 'bg-blue-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50'
								}`}>
								OR
							</button>
						</div>
					</div>
				)}
			</div>

			<div className='p-4 border border-neutral-200 rounded-lg bg-neutral-50'>
				<h3 className='text-sm font-medium text-neutral-700 mb-3'>Add Filter</h3>
				<div className='grid grid-cols-1 md:grid-cols-3 gap-3 mb-3'>
					<div>
						<label htmlFor='filter-field-select' className='block text-xs text-neutral-500 mb-1'>
							Field
						</label>
						<select
							id='filter-field-select'
							value={newFilter.field || ''}
							onChange={(e) => handleFieldChange(e.target.value)}
							className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'>
							<option value=''>Select field...</option>
							{Object.entries(groupedFields).map(
								([group, fields]) =>
									fields.length > 0 && (
										<optgroup key={group} label={group}>
											{fields.map((field) => (
												<option key={field.id} value={field.id}>
													{field.label}
													{field.type !== 'text' && field.type !== 'string' ? ` (${field.type})` : ''}
												</option>
											))}
										</optgroup>
									),
							)}
						</select>
					</div>
					<div>
						<label htmlFor='filter-operator-select' className='block text-xs text-neutral-500 mb-1'>
							Operator
						</label>
						<select
							id='filter-operator-select'
							value={newFilter.operator || FilterOperator.EQ}
							onChange={(e) => handleOperatorChange(e.target.value as FilterOperator)}
							className='w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
							disabled={!newFilter.field}>
							{operators.map((op) => (
								<option key={op.value} value={op.value}>
									{op.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<span className='block text-xs text-neutral-500 mb-1'>
							Value
							{isInOperator && !isSelectField && <span className='text-neutral-400 ml-1'>(add multiple)</span>}
						</span>
						{renderValueInput()}
					</div>
				</div>

				{validationError && (
					<div id='filter-validation-error' className='mb-3 p-2 bg-red-50 border border-red-200 rounded-md'>
						<p className='text-sm text-red-600'>{validationError}</p>
					</div>
				)}

				{(newFilter.values || []).length > 0 && !isNullOperator && (
					<div className='flex flex-wrap gap-2 mb-3'>
						{(newFilter.values || []).map((value, index) => (
							<span
								key={value}
								className='inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm'>
								{formatDisplayValue(value, newFilter.field || '')}
								{isBetweenOperator && index === 0 && <span className='mx-1 text-blue-400'>→</span>}
								{(!isBetweenOperator || index === (newFilter.values || []).length - 1) && (
									<button
										type='button'
										onClick={() => {
											if (isBetweenOperator) {
												setNewFilter({ ...newFilter, values: [] })
												setNewFilterValue('')
												setSecondValue('')
											} else {
												removeValueFromNewFilter(index)
											}
										}}
										className='ml-1 text-blue-500 hover:text-blue-700'>
										×
									</button>
								)}
							</span>
						))}
					</div>
				)}

				<button
					type='button'
					onClick={addFilter}
					disabled={!canAddFilter}
					className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
					Add Filter
				</button>
			</div>

			<div>
				<h3 className='text-sm font-medium text-neutral-700 mb-3'>Active Filters ({currentConfig.filters.length})</h3>
				{currentConfig.filters.length === 0 ? (
					<p className='text-sm text-neutral-500 p-4 bg-neutral-50 rounded-lg text-center'>
						No filters applied. All data will be included.
					</p>
				) : (
					<ul className='space-y-2'>
						{currentConfig.filters.map((filter, index) => (
							<li
								key={`${filter.field}-${filter.operator}-${index}`}
								className='flex items-center justify-between p-3 border border-neutral-200 rounded-md bg-white'>
								<div className='flex items-center gap-2 flex-wrap'>
									<span className='font-medium text-sm text-neutral-900'>{getFieldLabel(filter.field)}</span>
									<span className='text-sm text-neutral-500'>{getOperatorLabel(filter.operator)}</span>
									{filter.values.length > 0 && (
										<div className='flex flex-wrap gap-1'>
											{filter.values.map((value, vIdx) => (
												<span key={value} className='px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-sm'>
													{formatDisplayValue(value, filter.field)}
													{filter.operator === FilterOperator.BETWEEN && vIdx === 0 && filter.values.length > 1 && (
														<span className='mx-1'>→</span>
													)}
												</span>
											))}
										</div>
									)}
								</div>
								<button
									type='button'
									onClick={() => removeFilter(index)}
									aria-label={`Remove filter: ${getFieldLabel(filter.field)}`}
									className='text-neutral-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50'>
									<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
										<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
									</svg>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			{currentConfig.filters.length > 0 && (
				<div
					className={`p-3 border rounded-md ${filterLogic === 'AND' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
					<p className={`text-sm ${filterLogic === 'AND' ? 'text-blue-700' : 'text-purple-700'}`}>
						<strong>Filter Logic:</strong>{' '}
						{filterLogic === 'AND'
							? 'Results must match ALL filters (AND logic).'
							: 'Results must match ANY filter (OR logic).'}
					</p>
				</div>
			)}

			<div className='flex justify-between'>
				<button
					type='button'
					onClick={prevStep}
					className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
					Back
				</button>
				<button
					type='button'
					onClick={nextStep}
					className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium'>
					Next: Customize Labels
				</button>
			</div>
		</div>
	)
}
